import { useForm } from "react-hook-form";
import { FC, useEffect, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { OrganizationItemSchema } from "./data/OrganizationItemSchema";
import SearchTable from "../../components/template/Search/SearchTable";
import { useFilter } from "../../hooks/filter/useFilter";
import { ORGANIZATION_SUMMARY, PAGE_SIZE_OPTIONS } from "../../utils/constants";
import SelectGroup from "../../components/molecule/SelectGroup/SelectGroup";
import TableOrganization from "./TableOrganization";
import { getOrganization } from "../../services/getOrganization";
import { useAuth } from "../../provider/AuthProvider";
import { getProvince } from "../../services/TabelasAuxiliares/getProvince";
import { getCountyById } from "../../services/TabelasAuxiliares/getCountyById";
import { Option } from "../../types/General/Option";
import { organizationSelectFilterData } from "./data/organizationSelectFilterData";
import { FilterConfig } from "../../types/Components/Table/FilterConfig";
import { TableConfig } from "../../types/Components/Table/TableConfig";
import useFetchData from "../../hooks/data/useFetchData";
import useTableRendering from "../../hooks/table/useTableRendering";
import { PaginationConfig } from "../../types/Components/Table/PaginationConfig";
import { PaginationValues } from "../../types/General/PaginationValues";
import { getFiltersArray } from "../../helpers/getFiltersArray";

const Organization: FC = () => {
  const navigate = useNavigate();
  const [, setIdItem] = useState<number>(0);
  const [selectedFilterItems, setSelectedFilterItems] = useState<string>("");
  const [dataTable, setDataTable] = useState<any[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [errorFindingData, setErrorFindingData] = useState(false);
  const [selectedProvince, setSelectedProvince] = useState<Option | null>(null);
  const [provinceOptions, setProvinceOptions] = useState<Option[]>([]);
  const [selectedCounty, setSelectedCounty] = useState<string | null>("");
  const [countyOptions, setCountyOptions] = useState<Option[]>([]);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [isSearching, setIsSearching] = useState<boolean>(true);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [countyLoaded, setCountyLoaded] = useState<boolean>(false);
  const { auth } = useAuth();
  const user = auth.user;

  const { control, setValue } = useForm({
    resolver: zodResolver(OrganizationItemSchema),
  });

  const filterNames = useMemo(
    () => ["ProvinceId", "CountyId", "NameOrEmail"],
    [],
  );
  const { filters, setFilter } = useFilter({ setValue, filterNames });
  const { isLoaded: provinceLoaded } = useFetchData(
    getProvince,
    setProvinceOptions,
  );

  useEffect(() => {
    const fetchData = (
      filterValues?: any,
      paginationValues?: PaginationValues,
    ) => {
      setErrorFindingData(false);
      setIsLoaded(false);
      setTimeout(() => {
        getOrganization(undefined, filterValues, paginationValues)
          .then((result) => {
            setDataTable(result.content);
            setTotalItems(result.totalItems);
            setIsLoaded(true);
            setIsSearching(false);
          })
          .catch((error) => {
            console.log("API Error:", error);
            setErrorFindingData(true);
            setIsSearching(false);
          });
      }, 1000);
    };
    isSearching &&
      fetchData(filters, {
        CurrentPage: currentPage,
        PageSize: pageSize,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearching]);

  useEffect(() => {
    const updateSelectedFilterItemsName = () => {
      const province = filters["ProvinceId"] ? selectedProvince?.name : "";
      const county = filters["CountyId"] ? selectedCounty : "";
      setSelectedFilterItems([province, county].filter(Boolean).join(", "));
    };
    updateSelectedFilterItemsName();
  }, [selectedProvince, selectedCounty, filters]);

  useEffect(() => {
    const updateCountyOptions = () => {
      setCountyLoaded(false);
      if (selectedProvince) {
        getCountyById({ ParentId: selectedProvince.id })
          .then((result) => setCountyOptions(result))
          .finally(() => {
            setCountyLoaded(true);
          });
      } else {
        setCountyOptions([]);
        setCountyLoaded(true);
      }
    };
    updateCountyOptions();
  }, [selectedProvince]);

  const filtersArray = getFiltersArray(filterNames, filters, setFilter);

  const selectComponents = organizationSelectFilterData(
    provinceOptions,
    countyOptions,
    control,
    setFilter,
    setSelectedProvince,
    setSelectedCounty,
    selectedProvince,
    provinceLoaded,
    countyLoaded,
  );

  const filterChildren = (
    <SelectGroup selectComponents={selectComponents} width="230px" />
  );

  const handleAddNewItem = () => {
    navigate("/entidades/criar-entidades");
  };

  const tableConfig: TableConfig = {
    tableName: "Lista de Entidades",
    dataTable,
    setDataTable,
    renderTable: (dataTable) => (
      <TableOrganization
        dataTable={dataTable}
        isLoaded={isLoaded}
        setValue={setValue}
        setIdUpdateItem={setIdItem}
        user={user}
      />
    ),
    isLoaded,
    errorFindingData,
    searchTitle: "Pesquisar pelo Nome",
  };

  const filterConfig: FilterConfig = {
    filterChildren,
    filters: filtersArray,
    selectedFilterItem: selectedFilterItems,
  };

  const { currentPage, totalPages, handlePageChange, renderContent } =
    useTableRendering(tableConfig, pageSize, totalItems, setIsSearching);

  const paginationConfig: PaginationConfig = {
    pageSize,
    currentPage,
    totalPages,
    handlePageChange,
    renderContent,
    setPageSize,
    totalItems,
  };

  return (
    <SearchTable
      handleAddNewItem={
        user.permissions.includes("CreateOrganizations")
          ? handleAddNewItem
          : null
      }
      summaryName={ORGANIZATION_SUMMARY}
      showModal
      searchTableName="Entidades"
      createButtonName="Criar Entidade"
      tableConfig={tableConfig}
      filterConfig={filterConfig}
      paginationConfig={paginationConfig}
      setIsSearching={setIsSearching}
    ></SearchTable>
  );
};

export default Organization;
